import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useNavigate } from "react-router-dom";

import Header from "../../../../common/Header";
import Sidebar from "../../../../common/Sidebar";
import TableHeadComponent from "../../../../common/TableHeadComponent";
import { getComparator, mainLayoutStyles, routes, showNotification, stableSort } from "../../../../constants";
import { useAuth } from "../../../../hooks";
import SearchBar from "../../../OrganizationQuickTales/components/SearchBar/SearchBar";
import { getCustomNarratorsByUser, updateCustomNarrator } from "../../../OrganizationQuickTales/hooks/CustomNarratorsHooks";
import GuestContributorEditModal from "../../components/GuestContributorEditModal/GuestContributorEditModal";

import { serverTimestamp } from "firebase/firestore";

const formatCellValue = (value) => {
  if (value == null) return "";
  // Firestore Timestamp
  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return "";
    }
  }
  // JS Date (we use this for optimistic updatedAt)
  if (value instanceof Date) {
    return value.toLocaleString();
  }
  return String(value);
};

const getColumnOrderScore = (key) => {
  const order = ["name", "firstName", "lastName", "email", "createdAt", "updatedAt", "id"];
  const idx = order.indexOf(key);
  return idx === -1 ? 999 : idx;
};

const GuestContributorsContainer = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("updatedAt");

  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const ownerUserId = currentUser?.userId;

  const load = useCallback(async () => {
    if (!ownerUserId) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getCustomNarratorsByUser(ownerUserId);
      setRows(data);
      setFilteredRows(data);
    } catch (e) {
      console.error(e);
      showNotification("Error loading Guest Contributors");
    } finally {
      setLoading(false);
    }
  }, [ownerUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e) => {
    const value = e.target.value || "";
    if (!value) {
      setFilteredRows(rows);
      return;
    }
    const term = value.toLowerCase();
    const filtered = rows.filter((r) => {
      return (
        (r.id && String(r.id).toLowerCase().includes(term)) ||
        (r.name && String(r.name).toLowerCase().includes(term)) ||
        (r.firstName && String(r.firstName).toLowerCase().includes(term)) ||
        (r.lastName && String(r.lastName).toLowerCase().includes(term)) ||
        (r.email && String(r.email).toLowerCase().includes(term))
      );
    });
    setFilteredRows(filtered);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const columns = useMemo(() => {
    const keys = new Set();
    rows.forEach((r) => {
      Object.keys(r || {}).forEach((k) => keys.add(k));
    });
    // Ensure id always present
    keys.add("id");
    // Ensure updatedAt present
    keys.add("updatedAt");

    // Explicitly omit createdBy
    keys.delete("createdBy");

    return Array.from(keys).sort((a, b) => {
      const sa = getColumnOrderScore(a);
      const sb = getColumnOrderScore(b);
      if (sa !== sb) return sa - sb;
      return a.localeCompare(b);
    });
  }, [rows]);

  const headCells = useMemo(
    () =>
      columns.map((c) => ({
        id: c,
        label:
          c === "id"
            ? "ID"
            : c === "name"
            ? "Name"
            : c === "firstName"
            ? "First Name"
            : c === "lastName"
            ? "Last Name"
            : c === "email"
            ? "Email Address"
            : c === "createdAt"
            ? "Create Date"
            : c === "updatedAt"
            ? "Last Updated"
            : c,
        numeric: false,
        disablePadding: true,
        width: c === "id" ? 200 : c === "email" ? 200 : 140,
      })),
    [columns]
  );

  const handleRowClick = (row) => {
    setSelected(row);
    setEditOpen(true);
  };

  const handleClose = () => {
    setEditOpen(false);
    setSelected(null);
  };

  const handleSave = async (docId, payload, mergedRecord) => {
    // Optimistic UI update
    const optimisticRecord = { ...mergedRecord, updatedAt: new Date() };
    setRows((prev) => prev.map((r) => (r.id === docId ? optimisticRecord : r)));
    setFilteredRows((prev) => prev.map((r) => (r.id === docId ? optimisticRecord : r)));
    setEditOpen(false);

    try {
      await updateCustomNarrator(docId, { ...payload, updatedAt: serverTimestamp() });
    } catch (e) {
      console.error(e);
      // Re-load to ensure UI is accurate if update fails
      await load();
      showNotification(e?.message || "Error saving Guest Contributor");
    } finally {
      setSelected(null);
    }
  };

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <Header
        mainTitle={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              aria-label="Back"
              onClick={() => navigate(-1)}
              sx={{ color: "#00A7CD", mr: 1 }}
              size="small"
            >
              <ArrowBackIcon fontSize="medium" />
            </IconButton>
            <Box component="span">Guest Contributors</Box>
          </Box>
        }
        bgColor="#fff"
      />
      <Sidebar />

      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"}>
          <Box sx={{ mx: 2, mt: 2 }}>
            <SearchBar placeholder={"Search name, email, id"} handleSearch={handleSearch} />
          </Box>
          <Paper
            sx={{
              width: "100%",
              mb: 2,
              mx: 2,
              mt: 1,
              // Make only the table area scroll (match other table pages)
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(96vh - 120px)",
            }}
          >
            <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
              <Table
                stickyHeader
                sx={{ minWidth: 750 }}
                aria-labelledby="guestContributorsTable"
                size="medium"
              >
                <TableHead>
                  <TableHeadComponent
                    detailsPageReports={true}
                    TableHeadData={headCells}
                    numSelected={0}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={() => {}}
                    rowCount={filteredRows.length}
                  />
                </TableHead>
                <TableBody>
                  {filteredRows.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                        No Guest Contributors yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {stableSort(filteredRows, getComparator(order, orderBy)).map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleRowClick(row)}
                        >
                          {columns.map((col) => (
                            <TableCell key={`${row.id}-${col}`} sx={{ maxWidth: 320 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatCellValue(row[col])}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <GuestContributorEditModal
        open={editOpen}
        onClose={handleClose}
        record={selected}
        onSave={handleSave}
      />

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default GuestContributorsContainer;
